/**
 * =============================================================================
 * LGBT RESOURCES MAP COMPONENT
 * =============================================================================
 * 
 * Interactive map component for displaying LGBT+ resources from the 
 * LGBT National Help Center's database (glbtnearme.org).
 * 
 * FEATURES:
 * - Interactive Leaflet map with custom colored markers
 * - Search by ZIP code with adjustable radius (1-100 miles)
 * - Category filtering (16 categories based on API analysis)
 * - Youth Safe Mode (filters adult venues like bars/clubs)
 * - Multiple color themes (Purple, Inverted, Monochrome, Ocean)
 * - "Search This Area" button when user pans the map
 * - Mobile responsive with platform-specific directions (iOS/Android/Desktop)
 * 
 * MARKER COLOR SYSTEM:
 * - Each category has a unique color for easy identification
 * - RED (#FF3B30) is reserved ONLY for Crisis resources (urgent/emergency)
 * - Bar/Club uses AMBER (#E67E22) to distinguish from Crisis
 * - Adult venues (18+) show their category color with a small "18+" badge
 * 
 * API INTEGRATION:
 * - Backend: lgbt-widget-backend (Express.js)
 * - Source: glbtnearme.org API
 * - Authentication: API key passed via X-API-Key header
 * 
 * @author LGBT National Help Center Widget Team
 * @version 2.0.0
 * @see https://glbtnearme.org - Source database
 * =============================================================================
 */

import React, { useState, useEffect, useRef } from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMap, useMapEvents } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import './LGBTResourcesMap.css';

// =============================================================================
// CONFIGURATION
// =============================================================================

/**
 * API endpoint for the backend server.
 * Uses environment variable in production, falls back to Render deployment.
 */
const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://lgbt-widget-backend.onrender.com';

// =============================================================================
// THEME DEFINITIONS
// =============================================================================

/**
 * Color theme configurations for the map component.
 * 
 * Each theme includes:
 * - primary: Main brand color (used for headers, buttons)
 * - primaryRgb: RGB values for rgba() usage (e.g., transparent overlays)
 * - secondary: Complementary color
 * - accent: Highlight color for interactive elements
 * - light: Light variant for backgrounds
 * - gradient/gradientReverse: CSS gradients for decorative elements
 * 
 * USAGE:
 * - Pass theme prop to component: <LGBTResourcesMap theme="ocean" />
 * - CSS variables are automatically applied to container
 */
const THEME_COLORS = {
  // Default purple/green theme (LGBT National Help Center branding)
  purple: {
    primary: '#491994',
    primaryRgb: '73, 25, 148',
    secondary: '#5CB85C',
    secondaryRgb: '92, 184, 92',
    accent: '#af8bef',
    accentRgb: '175, 139, 239',
    light: '#7DCD7D',
    gradient: 'linear-gradient(135deg, #5CB85C 0%, #491994 100%)',
    gradientReverse: 'linear-gradient(135deg, #491994 0%, #5CB85C 100%)'
  },
  // Inverted theme (green primary)
  inverted: {
    primary: '#7DCD7D',
    primaryRgb: '125, 205, 125',
    secondary: '#af8bef',
    secondaryRgb: '175, 139, 239',
    accent: '#491994',
    accentRgb: '73, 25, 148',
    light: '#c9b3f7',
    gradient: 'linear-gradient(135deg, #af8bef 0%, #7DCD7D 100%)',
    gradientReverse: 'linear-gradient(135deg, #7DCD7D 0%, #af8bef 100%)'
  },
  // Monochrome theme (accessibility-friendly, no color reliance)
  monochrome: {
    primary: '#333333',
    primaryRgb: '51, 51, 51',
    secondary: '#666666',
    secondaryRgb: '102, 102, 102',
    accent: '#999999',
    accentRgb: '153, 153, 153',
    light: '#cccccc',
    gradient: 'linear-gradient(135deg, #666666 0%, #333333 100%)',
    gradientReverse: 'linear-gradient(135deg, #333333 0%, #666666 100%)'
  },
  // Ocean theme (blue tones)
  ocean: {
    primary: '#1e3c72',
    primaryRgb: '30, 60, 114',
    secondary: '#2a5298',
    secondaryRgb: '42, 82, 152',
    accent: '#5b8cdf',
    accentRgb: '91, 140, 223',
    light: '#7ba3e8',
    gradient: 'linear-gradient(135deg, #2a5298 0%, #1e3c72 100%)',
    gradientReverse: 'linear-gradient(135deg, #1e3c72 0%, #2a5298 100%)'
  }
};

// =============================================================================
// SOCIAL MEDIA PLATFORM DETECTION WITH BRAND ICONS
// =============================================================================

/**
 * Returns an SVG icon component for a given platform.
 * All icons are 16x16 and styled consistently.
 */
const PlatformIcon = ({ platform }) => {
  const iconStyle = {
    width: '16px',
    height: '16px',
    flexShrink: 0,
    display: 'inline-block',
    verticalAlign: 'middle'
  };

  switch (platform) {
    case 'instagram':
      return (
        <svg style={iconStyle} viewBox="0 0 24 24" fill="#E4405F">
          <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
        </svg>
      );
    case 'twitter':
      return (
        <svg style={iconStyle} viewBox="0 0 24 24" fill="#000000">
          <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
        </svg>
      );
    case 'facebook':
      return (
        <svg style={iconStyle} viewBox="0 0 24 24" fill="#1877F2">
          <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
        </svg>
      );
    case 'youtube':
      return (
        <svg style={iconStyle} viewBox="0 0 24 24" fill="#FF0000">
          <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
        </svg>
      );
    case 'tiktok':
      return (
        <svg style={iconStyle} viewBox="0 0 24 24" fill="#000000">
          <path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z"/>
        </svg>
      );
    case 'linkedin':
      return (
        <svg style={iconStyle} viewBox="0 0 24 24" fill="#0A66C2">
          <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
        </svg>
      );
    case 'threads':
      return (
        <svg style={iconStyle} viewBox="0 0 24 24" fill="#000000">
          <path d="M12.186 24h-.007c-3.581-.024-6.334-1.205-8.184-3.509C2.35 18.44 1.5 15.586 1.472 12.01v-.017c.03-3.579.879-6.43 2.525-8.482C5.845 1.205 8.6.024 12.18 0h.014c2.746.02 5.043.725 6.826 2.098 1.677 1.29 2.858 3.13 3.509 5.467l-2.04.569c-1.104-3.96-3.898-5.984-8.304-6.015-2.91.022-5.11.936-6.54 2.717C4.307 6.504 3.616 8.914 3.59 12c.025 3.086.718 5.496 2.057 7.164 1.43 1.783 3.631 2.698 6.54 2.717 2.623-.02 4.358-.631 5.8-2.045 1.647-1.613 1.618-3.593 1.09-4.798-.31-.71-.873-1.3-1.634-1.75-.192 1.352-.622 2.446-1.284 3.272-.886 1.102-2.14 1.704-3.73 1.79-1.202.065-2.361-.218-3.259-.801-1.063-.689-1.685-1.74-1.752-2.96-.065-1.182.408-2.256 1.332-3.023.88-.73 2.088-1.146 3.396-1.17 1.078-.02 2.074.173 2.96.566l.025-.108c.072-.313.109-.682.109-1.097 0-1.408-.396-2.406-1.176-2.967-.652-.47-1.544-.669-2.726-.608-.895.046-1.63.097-2.252.672-.49.453-.81 1.14-.904 1.912l-2.006-.096c.073-1.347.57-2.471 1.439-3.249.966-.865 2.24-1.322 3.682-1.32 1.573-.006 2.824.403 3.72 1.216 1.01.915 1.523 2.29 1.523 4.086 0 .455-.044.88-.128 1.272l.022.01c1.222.596 2.282 1.474 2.897 2.888.773 1.781.744 4.548-1.496 6.74-1.924 1.883-4.292 2.704-7.453 2.736z"/>
        </svg>
      );
    case 'discord':
      return (
        <svg style={iconStyle} viewBox="0 0 24 24" fill="#5865F2">
          <path d="M20.317 4.3698a19.7913 19.7913 0 00-4.8851-1.5152.0741.0741 0 00-.0785.0371c-.211.3753-.4447.8648-.6083 1.2495-1.8447-.2762-3.68-.2762-5.4868 0-.1636-.3933-.4058-.8742-.6177-1.2495a.077.077 0 00-.0785-.037 19.7363 19.7363 0 00-4.8852 1.515.0699.0699 0 00-.0321.0277C.5334 9.0458-.319 13.5799.0992 18.0578a.0824.0824 0 00.0312.0561c2.0528 1.5076 4.0413 2.4228 5.9929 3.0294a.0777.0777 0 00.0842-.0276c.4616-.6304.8731-1.2952 1.226-1.9942a.076.076 0 00-.0416-.1057c-.6528-.2476-1.2743-.5495-1.8722-.8923a.077.077 0 01-.0076-.1277c.1258-.0943.2517-.1923.3718-.2914a.0743.0743 0 01.0776-.0105c3.9278 1.7933 8.18 1.7933 12.0614 0a.0739.0739 0 01.0785.0095c.1202.099.246.1981.3728.2924a.077.077 0 01-.0066.1276 12.2986 12.2986 0 01-1.873.8914.0766.0766 0 00-.0407.1067c.3604.698.7719 1.3628 1.225 1.9932a.076.076 0 00.0842.0286c1.961-.6067 3.9495-1.5219 6.0023-3.0294a.077.077 0 00.0313-.0552c.5004-5.177-.8382-9.6739-3.5485-13.6604a.061.061 0 00-.0312-.0286zM8.02 15.3312c-1.1825 0-2.1569-1.0857-2.1569-2.419 0-1.3332.9555-2.4189 2.157-2.4189 1.2108 0 2.1757 1.0952 2.1568 2.419 0 1.3332-.9555 2.4189-2.1569 2.4189zm7.9748 0c-1.1825 0-2.1569-1.0857-2.1569-2.419 0-1.3332.9554-2.4189 2.1569-2.4189 1.2108 0 2.1757 1.0952 2.1568 2.419 0 1.3332-.946 2.4189-2.1568 2.4189z"/>
        </svg>
      );
    case 'pinterest':
      return (
        <svg style={iconStyle} viewBox="0 0 24 24" fill="#BD081C">
          <path d="M12.017 0C5.396 0 .029 5.367.029 11.987c0 5.079 3.158 9.417 7.618 11.162-.105-.949-.199-2.403.041-3.439.219-.937 1.406-5.957 1.406-5.957s-.359-.72-.359-1.781c0-1.663.967-2.911 2.168-2.911 1.024 0 1.518.769 1.518 1.688 0 1.029-.653 2.567-.992 3.992-.285 1.193.6 2.165 1.775 2.165 2.128 0 3.768-2.245 3.768-5.487 0-2.861-2.063-4.869-5.008-4.869-3.41 0-5.409 2.562-5.409 5.199 0 1.033.394 2.143.889 2.741.099.12.112.225.085.345-.09.375-.293 1.199-.334 1.363-.053.225-.172.271-.401.165-1.495-.69-2.433-2.878-2.433-4.646 0-3.776 2.748-7.252 7.92-7.252 4.158 0 7.392 2.967 7.392 6.923 0 4.135-2.607 7.462-6.233 7.462-1.214 0-2.354-.629-2.758-1.379l-.749 2.848c-.269 1.045-1.004 2.352-1.498 3.146 1.123.345 2.306.535 3.55.535 6.607 0 11.985-5.365 11.985-11.987C23.97 5.39 18.592.026 11.985.026L12.017 0z"/>
        </svg>
      );
    case 'tumblr':
      return (
        <svg style={iconStyle} viewBox="0 0 24 24" fill="#36465D">
          <path d="M14.563 24c-5.093 0-7.031-3.756-7.031-6.411V9.747H5.116V6.648c3.63-1.313 4.512-4.596 4.71-6.469C9.84.051 9.941 0 9.999 0h3.517v6.114h4.801v3.633h-4.82v7.47c.016 1.001.375 2.371 2.207 2.371h.09c.631-.02 1.486-.205 1.936-.419l1.156 3.425c-.436.636-2.4 1.374-4.156 1.404h-.178z"/>
        </svg>
      );
    case 'reddit':
      return (
        <svg style={iconStyle} viewBox="0 0 24 24" fill="#FF4500">
          <path d="M12 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0zm5.01 4.744c.688 0 1.25.561 1.25 1.249a1.25 1.25 0 0 1-2.498.056l-2.597-.547-.8 3.747c1.824.07 3.48.632 4.674 1.488.308-.309.73-.491 1.207-.491.968 0 1.754.786 1.754 1.754 0 .716-.435 1.333-1.01 1.614a3.111 3.111 0 0 1 .042.52c0 2.694-3.13 4.87-7.004 4.87-3.874 0-7.004-2.176-7.004-4.87 0-.183.015-.366.043-.534A1.748 1.748 0 0 1 4.028 12c0-.968.786-1.754 1.754-1.754.463 0 .898.196 1.207.49 1.207-.883 2.878-1.43 4.744-1.487l.885-4.182a.342.342 0 0 1 .14-.197.35.35 0 0 1 .238-.042l2.906.617a1.214 1.214 0 0 1 1.108-.701zM9.25 12C8.561 12 8 12.562 8 13.25c0 .687.561 1.248 1.25 1.248.687 0 1.248-.561 1.248-1.249 0-.688-.561-1.249-1.249-1.249zm5.5 0c-.687 0-1.248.561-1.248 1.25 0 .687.561 1.248 1.249 1.248.688 0 1.249-.561 1.249-1.249 0-.687-.562-1.249-1.25-1.249zm-5.466 3.99a.327.327 0 0 0-.231.094.33.33 0 0 0 0 .463c.842.842 2.484.913 2.961.913.477 0 2.105-.056 2.961-.913a.361.361 0 0 0 .029-.463.33.33 0 0 0-.464 0c-.547.533-1.684.73-2.512.73-.828 0-1.979-.196-2.512-.73a.326.326 0 0 0-.232-.095z"/>
        </svg>
      );
    case 'meetup':
      return (
        <svg style={iconStyle} viewBox="0 0 24 24" fill="#ED1C40">
          <path d="M6.98 12.13c-.05.2-.1.39-.17.57-.26.64-.67 1.05-1.3 1.22-.61.17-1.18.04-1.66-.38-.18-.15-.33-.34-.5-.52.02-.05.03-.08.05-.1.49-.63.99-1.26 1.48-1.89.15-.18.29-.37.44-.55.32-.4.79-.53 1.27-.41.49.12.8.46.88.97.04.26.01.51-.07.76-.14.44-.28.88-.42 1.33zm2.33 1.02c.42.17.84.34 1.27.52-.01.1-.03.18-.05.27-.18.72-.38 1.44-.56 2.16-.11.43-.21.86-.34 1.28-.25.81-.93 1.32-1.76 1.33-.74.01-1.41-.45-1.68-1.16-.08-.2-.12-.43-.12-.65-.01-.79.03-1.59.05-2.38 0-.07.03-.15.06-.22.04-.1.1-.1.14.01.31.88.62 1.76.93 2.64.03.1.06.19.11.29.23.52.81.73 1.3.49.49-.25.73-.82.55-1.34-.18-.53-.36-1.05-.54-1.58-.08-.24-.15-.47-.23-.71-.02-.07-.04-.14-.06-.21-.04-.14.01-.2.14-.15.24.1.48.19.72.28.31.11.63.22.94.34.31.12.61.24.91.37.25.11.5.23.74.35-.04.1-.07.2-.11.3-.28.71-.56 1.43-.85 2.14-.2.5-.42.99-.61 1.49-.23.62-.73 1.02-1.39 1.11-.73.1-1.33-.18-1.74-.79-.22-.33-.33-.71-.32-1.1.02-.8.06-1.59.1-2.39.02-.42.05-.83.08-1.25.01-.1.04-.14.14-.1.57.23 1.15.47 1.72.7zm8.27-2.58c-.02.2-.04.4-.06.61-.12.92-.39 1.8-.79 2.64-.39.82-.91 1.55-1.57 2.17-.42.4-.88.73-1.42.96-.53.22-1.07.29-1.65.21-.51-.07-.94-.29-1.27-.67-.37-.42-.54-.91-.51-1.47.02-.4.12-.78.3-1.14.32-.64.77-1.19 1.28-1.68.78-.74 1.66-1.35 2.59-1.9.34-.2.68-.4 1.03-.58.05-.03.12-.04.18-.03.03 0 .07.06.08.1.06.26.08.52.08.78-.01.33-.02.67-.04 1zm2.77-.06c.13.19.11.41.06.63-.13.59-.33 1.16-.57 1.71-.38.86-.85 1.67-1.39 2.44-.36.52-.77 1-1.23 1.44-.52.5-1.11.89-1.8 1.13-.49.17-.99.25-1.51.17-.84-.14-1.4-.62-1.68-1.41-.08-.23-.12-.48-.13-.73-.02-.62.11-1.21.36-1.78.25-.58.58-1.11.99-1.58.55-.64 1.21-1.16 1.94-1.59.49-.29 1-.55 1.53-.75.65-.25 1.33-.4 2.03-.38.48.01.9.14 1.21.48.15.17.22.38.19.62-.04.4-.14.78-.31 1.14-.25.54-.59 1.03-1 1.46-.59.62-1.27 1.13-2.04 1.5-.04.02-.08.04-.13.05-.02.01-.05 0-.09-.01.02-.05.03-.09.06-.12.48-.55.87-1.14 1.16-1.8.17-.39.3-.8.39-1.22.03-.15.05-.31.06-.46.01-.11-.04-.17-.15-.18-.28-.01-.54.06-.8.15-.82.28-1.57.71-2.26 1.24-.6.46-1.11 1-1.52 1.63-.23.35-.41.73-.48 1.15-.03.16-.03.33-.02.5.02.28.18.45.46.49.24.03.47-.02.7-.1.58-.22 1.1-.55 1.57-.95.84-.73 1.52-1.58 2.06-2.55.26-.46.48-.94.65-1.44.12-.35.21-.71.26-1.08.02-.15 0-.3-.05-.44-.09-.26-.32-.34-.55-.21-.49.27-.95.58-1.37.93-.79.67-1.46 1.44-2.01 2.31-.08.12-.14.26-.21.39-.01.02-.02.05-.04.1-.04-.06-.08-.1-.1-.15-.22-.42-.25-.86-.11-1.31.16-.55.47-1.02.88-1.42.61-.59 1.32-1.04 2.1-1.39.56-.25 1.14-.44 1.74-.55.35-.07.7-.1 1.06-.06.26.03.49.12.67.33z"/>
        </svg>
      );
    case 'eventbrite':
      return (
        <svg style={iconStyle} viewBox="0 0 24 24" fill="#F05537">
          <path d="M16.296 12.763a5.47 5.47 0 0 1-.197 1.32H8.07c.276 1.376 1.392 2.367 2.77 2.367.904 0 1.727-.393 2.317-1.066l2.138 1.428c-1.05 1.393-2.666 2.188-4.455 2.188-3.122 0-5.51-2.544-5.51-5.735 0-3.19 2.388-5.734 5.51-5.734 2.994 0 5.456 2.544 5.456 5.232zM10.84 9.97c-1.26 0-2.308.874-2.663 2.112h5.162c-.236-1.238-1.282-2.112-2.5-2.112zM24 12c0 6.627-5.373 12-12 12S0 18.627 0 12 5.373 0 12 0s12 5.373 12 12z"/>
        </svg>
      );
    default:
      // Generic link icon
      return (
        <svg style={iconStyle} viewBox="0 0 24 24" fill="currentColor">
          <path d="M3.9 12c0-1.71 1.39-3.1 3.1-3.1h4V7H7c-2.76 0-5 2.24-5 5s2.24 5 5 5h4v-1.9H7c-1.71 0-3.1-1.39-3.1-3.1zM8 13h8v-2H8v2zm9-6h-4v1.9h4c1.71 0 3.1 1.39 3.1 3.1s-1.39 3.1-3.1 3.1h-4V17h4c2.76 0 5-2.24 5-5s-2.24-5-5-5z"/>
        </svg>
      );
  }
};

/**
 * Detects the social media platform from a URL and returns the platform key.
 * @param {string} url - The website URL to analyze
 * @returns {string} Platform key for icon lookup
 */
const getPlatformFromUrl = (url) => {
  if (!url) return 'default';
  
  const lowerUrl = url.toLowerCase();
  
  if (lowerUrl.includes('instagram.com') || lowerUrl.includes('instagr.am')) return 'instagram';
  if (lowerUrl.includes('twitter.com') || lowerUrl.includes('x.com')) return 'twitter';
  if (lowerUrl.includes('facebook.com') || lowerUrl.includes('fb.com') || lowerUrl.includes('fb.me')) return 'facebook';
  if (lowerUrl.includes('youtube.com') || lowerUrl.includes('youtu.be')) return 'youtube';
  if (lowerUrl.includes('tiktok.com')) return 'tiktok';
  if (lowerUrl.includes('linkedin.com')) return 'linkedin';
  if (lowerUrl.includes('threads.net')) return 'threads';
  if (lowerUrl.includes('discord.gg') || lowerUrl.includes('discord.com')) return 'discord';
  if (lowerUrl.includes('pinterest.com') || lowerUrl.includes('pin.it')) return 'pinterest';
  if (lowerUrl.includes('tumblr.com')) return 'tumblr';
  if (lowerUrl.includes('reddit.com')) return 'reddit';
  if (lowerUrl.includes('meetup.com')) return 'meetup';
  if (lowerUrl.includes('eventbrite.com')) return 'eventbrite';
  
  return 'default';
};

/**
 * Gets the display label for a platform.
 * @param {string} platform - Platform key
 * @returns {string} Human-readable label
 */
const getPlatformLabel = (platform) => {
  const labels = {
    instagram: 'Instagram',
    twitter: 'Twitter',
    facebook: 'Facebook',
    youtube: 'YouTube',
    tiktok: 'TikTok',
    linkedin: 'LinkedIn',
    threads: 'Threads',
    discord: 'Discord',
    pinterest: 'Pinterest',
    tumblr: 'Tumblr',
    reddit: 'Reddit',
    meetup: 'Meetup',
    eventbrite: 'Eventbrite',
    default: 'Website'
  };
  return labels[platform] || 'Website';
};

// =============================================================================
// UTILITY FUNCTIONS
// =============================================================================

/**
 * Generates a platform-appropriate directions URL.
 * 
 * Different platforms have different URL schemes for opening maps:
 * - iOS: Apple Maps (http://maps.apple.com)
 * - Android: geo: scheme (triggers "Open with" dialog)
 * - Desktop: Google Maps in browser
 * 
 * @param {string} address - The destination address (human-readable)
 * @param {number} lat - Latitude coordinate (optional, improves accuracy)
 * @param {number} lng - Longitude coordinate (optional, improves accuracy)
 * @param {string} platform - Target platform: 'apple', 'google', 'android', or 'desktop'
 * @returns {string|null} The directions URL, or null if platform not specified
 * 
 * @example
 * // iOS user - opens Apple Maps
 * getDirectionsUrl('123 Main St, NYC', 40.7128, -74.0060, 'apple')
 * // Returns: "http://maps.apple.com/?daddr=40.7128,-74.0060&q=123%20Main%20St%2C%20NYC"
 */
const getDirectionsUrl = (address, lat, lng, platform = null) => {
  const encodedAddress = encodeURIComponent(address);
  
  if (platform === 'apple') {
    // Apple Maps - native iOS experience
    if (lat && lng) {
      return `http://maps.apple.com/?daddr=${lat},${lng}&q=${encodedAddress}`;
    }
    return `http://maps.apple.com/?daddr=${encodedAddress}`;
  } else if (platform === 'google' || platform === 'desktop') {
    // Google Maps - works in browser or triggers app on mobile
    if (lat && lng) {
      return `https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}`;
    }
    return `https://www.google.com/maps/dir/?api=1&destination=${encodedAddress}`;
  } else if (platform === 'android') {
    // Android geo: scheme - triggers system "Open with" dialog
    // This lets users choose their preferred maps app
    if (lat && lng) {
      return `geo:${lat},${lng}?q=${encodedAddress}`;
    }
    return `geo:0,0?q=${encodedAddress}`;
  }
  
  return null;
};

/**
 * Detects the user's platform based on user agent string.
 * Used to provide platform-appropriate directions experience.
 * 
 * @returns {string} 'android', 'ios', or 'desktop'
 */
const getPlatform = () => {
  const isAndroid = /Android/i.test(navigator.userAgent);
  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
  
  if (isAndroid) return 'android';
  if (isIOS) return 'ios';
  return 'desktop';
};

// =============================================================================
// ZOOM LEVEL <-> RADIUS MAPPING
// =============================================================================

/**
 * Maps Leaflet zoom levels to search radius values.
 * 
 * This mapping is used for:
 * 1. Zoom indicator display (shows "Level X | Y mi")
 * 2. "Search This Area" - determines radius for new search based on zoom
 * 
 * Higher zoom = more zoomed in = smaller radius
 * Lower zoom = more zoomed out = larger radius
 */
const ZOOM_TO_RADIUS = {
  17: { level: 1, miles: 1 },   // Street level
  16: { level: 1, miles: 1 },
  15: { level: 2, miles: 5 },   // Neighborhood level
  14: { level: 2, miles: 5 },
  13: { level: 3, miles: 10 },  // City level
  12: { level: 3, miles: 10 },
  11: { level: 4, miles: 25 },  // Metro area level
  10: { level: 4, miles: 25 },
  9: { level: 5, miles: 50 },   // Regional level
  8: { level: 5, miles: 50 },
  7: { level: 6, miles: 100 },  // State level
  6: { level: 6, miles: 100 },
  5: { level: 6, miles: 100 },
};

/**
 * Gets the appropriate search radius info based on current zoom level.
 * 
 * @param {number} zoom - Current Leaflet map zoom level (1-20)
 * @returns {object} Object with { level: number, miles: number }
 * 
 * @example
 * getRadiusFromZoom(15) // Returns { level: 2, miles: 5 }
 * getRadiusFromZoom(9)  // Returns { level: 5, miles: 50 }
 */
const getRadiusFromZoom = (zoom) => {
  // Sort zoom levels descending to find closest match
  const zoomLevels = Object.keys(ZOOM_TO_RADIUS).map(Number).sort((a, b) => b - a);
  const closestZoom = zoomLevels.find(z => zoom >= z) || zoomLevels[zoomLevels.length - 1];
  return ZOOM_TO_RADIUS[closestZoom];
};

/**
 * Calculates distance between two geographic points using the Haversine formula.
 * 
 * The Haversine formula accounts for the Earth's curvature, providing
 * accurate distances for any two points on the globe.
 * 
 * @param {number} lat1 - Latitude of first point (degrees)
 * @param {number} lng1 - Longitude of first point (degrees)
 * @param {number} lat2 - Latitude of second point (degrees)
 * @param {number} lng2 - Longitude of second point (degrees)
 * @returns {number} Distance in miles
 * 
 * @example
 * // Distance from NYC to LA
 * calculateDistance(40.7128, -74.0060, 34.0522, -118.2437) // ~2,451 miles
 */
const calculateDistance = (lat1, lng1, lat2, lng2) => {
  const R = 3959; // Earth's radius in miles (use 6371 for kilometers)
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLng = (lng2 - lng1) * Math.PI / 180;
  const a = 
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
    Math.sin(dLng / 2) * Math.sin(dLng / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
};

// =============================================================================
// HELPER COMPONENTS
// =============================================================================

/**
 * MapMovementTracker Component
 * 
 * Invisible component that tracks map pan/zoom events and determines
 * when to show the "Search This Area" button.
 * 
 * LOGIC:
 * - Tracks distance from original search center
 * - When user moves beyond the original search radius, shows button
 * - Uses debouncing (500ms) to avoid excessive calculations during pan
 * 
 * @param {array} originalCenter - [lat, lng] of the original search location
 * @param {number} originalRadius - Original search radius in miles
 * @param {function} onMapMove - Callback when significant movement detected
 * @param {function} onZoomChange - Callback when zoom level changes
 * @param {boolean} hasSearched - Whether a search has been performed
 */
const MapMovementTracker = ({ 
  originalCenter, 
  originalRadius, 
  onMapMove, 
  onZoomChange,
  hasSearched 
}) => {
  const map = useMap();
  const moveTimeoutRef = useRef(null);

  // Cleanup timeout on unmount to prevent memory leaks
  useEffect(() => {
    return () => {
      if (moveTimeoutRef.current) {
        clearTimeout(moveTimeoutRef.current);
      }
    };
  }, []);

  // Subscribe to Leaflet map events
  useMapEvents({
    // Called when map stops moving (after pan or zoom)
    moveend: () => {
      // Don't track if no search has been performed yet
      if (!hasSearched || !originalCenter) return;
      
      // Debounce: clear previous timeout if still pending
      if (moveTimeoutRef.current) {
        clearTimeout(moveTimeoutRef.current);
      }

      // Wait 500ms after movement stops before calculating
      moveTimeoutRef.current = setTimeout(() => {
        try {
          // Safety check - map may have been unmounted
          if (!map || !map.getCenter) return;
          
          const currentCenter = map.getCenter();
          const currentZoom = map.getZoom();
          
          if (!currentCenter) return;
          
          // Calculate how far user has moved from original search
          const distance = calculateDistance(
            originalCenter[0],
            originalCenter[1],
            currentCenter.lat,
            currentCenter.lng
          );

          // Determine if user has moved outside the search radius
          const isOutsideRadius = distance > originalRadius;

          // Notify parent component
          onMapMove({
            center: [currentCenter.lat, currentCenter.lng],
            zoom: currentZoom,
            isOutsideRadius,
            distance
          });
        } catch (e) {
          // Map may have been unmounted during timeout
          console.log('Map not ready or unmounted');
        }
      }, 500);
    },
    
    // Called when zoom animation completes
    zoomend: () => {
      try {
        if (!map || !map.getZoom) return;
        const currentZoom = map.getZoom();
        onZoomChange(currentZoom);
      } catch (e) {
        // Map may have been unmounted
      }
    }
  });

  // This component renders nothing - it only handles events
  return null;
};

/**
 * MapController Component
 * 
 * Controls the map view (center and zoom) when search results change.
 * Uses Leaflet's flyTo() for smooth animated transitions.
 * 
 * IMPORTANT: Can be disabled via `enabled` prop to prevent the map
 * from jumping back to original position during "Search This Area".
 * 
 * @param {array} center - [lat, lng] to center the map on
 * @param {number} zoom - Target zoom level (1-20)
 * @param {array} resources - Array of resource objects (unused, kept for future)
 * @param {number} searchRadius - Current search radius in miles
 * @param {boolean} enabled - Whether to enable map control (default: true)
 */
const MapController = ({ center, zoom, resources, searchRadius, enabled = true }) => {
  const map = useMap();

  useEffect(() => {
    // Skip if disabled or no center provided
    if (!enabled || !center) {
      console.log('MapController disabled or no center');
      return;
    }

    console.log('MapController - setting view to center:', center, 'zoom:', zoom, 'radius:', searchRadius);
    
    // Animate map to new position
    map.flyTo(center, zoom, {
      animate: true,
      duration: 0.8 // 800ms animation
    });
    
    // WORKAROUND: Sometimes flyTo doesn't apply zoom correctly
    // Force zoom after animation completes as fallback
    setTimeout(() => {
      if (map.getZoom() !== zoom) {
        console.log('Zoom mismatch, forcing zoom to:', zoom);
        map.setZoom(zoom);
      }
    }, 900);
    
  }, [map, center, zoom, searchRadius, enabled]);

  return null;
};

/**
 * LoadingAnimation Component
 * 
 * Displays a themed loading overlay while search is in progress.
 * Features an animated dot traveling around a square path.
 * 
 * The animation uses CSS keyframes defined in LGBTResourcesMap.css
 * 
 * @param {object} themeColors - Color configuration for current theme
 */
const LoadingAnimation = ({ themeColors }) => {
  return (
    <div className="loading-overlay" style={{ 
      background: `rgba(${themeColors.primaryRgb}, 0.95)` 
    }}>
      <div className="loading-container">
        <div className="loading-square" style={{
          borderColor: `rgba(${themeColors.accentRgb}, 0.3)`
        }}>
          <div className="loading-dot" style={{
            background: themeColors.gradient
          }}></div>
          <div className="loading-trail"></div>
        </div>
        <p className="loading-text">Searching for resources...</p>
      </div>
    </div>
  );
};

// =============================================================================
// POSTAL CODE HELPERS
// =============================================================================
// Supports both US ZIP codes (12345 or 12345-6789) and Canadian postal codes
// (A1A 1A1 or A1A1A1). Added May 2026 in response to Aaron's report that
// Canadian visitors could not search.
// =============================================================================

const US_ZIP_REGEX = /^\d{5}(-\d{4})?$/;
const CA_POSTAL_REGEX = /^[A-Za-z]\d[A-Za-z]\s*\d[A-Za-z]\d$/;

/**
 * Returns true if the given string is a valid US ZIP or Canadian postal code.
 */
const isValidPostalCode = (code) => {
  if (!code) return false;
  const trimmed = code.trim();
  return US_ZIP_REGEX.test(trimmed) || CA_POSTAL_REGEX.test(trimmed);
};

/**
 * Returns 'US' or 'CA' depending on which format the code matches, or null.
 */
const getPostalCountry = (code) => {
  if (typeof code !== 'string') return null;
  const trimmed = code.trim();
  if (US_ZIP_REGEX.test(trimmed)) return 'US';
  if (CA_POSTAL_REGEX.test(trimmed)) return 'CA';
  return null;
};

/**
 * Normalizes a postal code for sending upstream.
 *
 * Canadian codes: STRIP any space and uppercase ("M5V 3A8" -> "M5V3A8").
 * Required because Tim's API (confirmed 22 May 2026) only matches
 * Canadian codes when sent as a 6-character no-space string.
 *
 * US ZIP+4: STRIP the +4 extension ("10011-1234" -> "10011"). Tim's API
 * only handles 5-digit US ZIPs, so we drop the extension and search the
 * 5-digit area. Better than returning 0 results to the user.
 *
 * US 5-digit ZIPs: returned trimmed and uppercased.
 */
const normalizePostalCode = (code) => {
  if (typeof code !== 'string') return '';
  const trimmed = code.trim().toUpperCase();
  if (/^[A-Z]\d[A-Z]\s*\d[A-Z]\d$/.test(trimmed)) {
    return trimmed.replace(/\s+/g, '');
  }
  if (/^\d{5}-\d{4}$/.test(trimmed)) {
    return trimmed.slice(0, 5);
  }
  return trimmed;
};

// =============================================================================
// SUBCATEGORY CONFIGURATION
// =============================================================================
// Curated category -> subcategory mapping from live analysis of Tim's API
// (find-all-subcategories-v2.js, July 2026). Junk single-letter values
// ("g", "n", "r", "t") and one-off values removed. Strings must EXACTLY
// match the API's subtype values, including existing typos in Tim's data
// (e.g. "Police Liason") -- do not correct spellings or filtering breaks.
// NOTE: this block is duplicated in LGBTResourcesWidget.js and
// LGBTResourcesMap.js -- keep both in sync when editing.
// =============================================================================

const ALL_SUBCATEGORIES_LABEL = 'All Subcategories';

// =============================================================================
// Sub-category SEARCH is disabled (Aug 2026): Tim wants sub-categories shown
// on the result cards themselves, not offered as a search filter.
// Flip this to true to bring the search dropdown back — everything underneath
// still works (backend filter support was left in place).
// =============================================================================
const SHOW_SUBCATEGORY_SEARCH = false;

/**
 * Build the combined Category-Subcategory badge labels for a resource,
 * per Brad's spec (Aug 2026): e.g. "Media-Magazine", "Social-Gay Men".
 *
 * REWRITTEN 5 Aug 2026: now built from the positional type1..8/subtype1..8
 * pairs (forwarded by the backend in `resource.types`), which are stable
 * across every query mode. The previous version read Tim's `subcategories`
 * array, but that field is QUERY-RELATIVE — it only contains the subtypes
 * belonging to the category you searched under — which caused the
 * inconsistent badges Brad reported on 4 Aug 2026 (23.5% of resources
 * showed different subcategory data depending on the search; see
 * scripts/diagnose-subcategory-inconsistency.js in the backend).
 *
 * Rules:
 *  - Each (type_i, subtype_i) pair becomes one "Type-Subtype" bubble.
 *  - Junk subtype codes (single chars like "t"/"n"/"g" in Tim's data) and
 *    subtypes identical to their type are treated as no subtype.
 *  - A type with no valid subtype gets a plain bubble, but only if that
 *    type has no combined bubble already (no bare "Social" beside
 *    "Social-Gay Men").
 *  - Pairs are deduped case-insensitively, original order preserved.
 *
 * NOTE: duplicated in LGBTResourcesWidget.js and LGBTResourcesMap.js —
 * keep both in sync. Unit tests with real API fixtures live in
 * lgbt-resources-widget-backend/scripts/test-badge-builder.js.
 *
 * @param {Object} resource - Formatted resource from the backend
 * @returns {string[]} Badge labels in display order
 */
const buildCategoryBadgeLabels = (resource) => {
  if (!resource) return [];

  // Backend forwards raw pairs nested under `types`; fall back to flat
  // fields in case a raw API-shaped object ever reaches this function.
  const pairSource = resource.types || resource;

  const combined = [];          // { label, typeKey }
  const bareTypes = [];         // { label, key }
  const seenPairKeys = new Set();
  const typesWithPairs = new Set();

  for (let i = 1; i <= 8; i++) {
    const rawType = pairSource[`type${i}`];
    const rawSub = pairSource[`subtype${i}`];

    const type = typeof rawType === 'string' ? rawType.trim() : '';
    if (!type) continue;

    const sub = typeof rawSub === 'string' ? rawSub.trim() : '';
    const typeKey = type.toLowerCase();

    // Junk filter: Tim's data contains stray single-char subtype codes
    // ("t", "n", "g") that his own cleaning also strips. Also skip
    // subtypes identical to their type ("Lesbian-Lesbian").
    const subIsValid = sub.length >= 2 && sub.toLowerCase() !== typeKey;

    if (subIsValid) {
      const pairKey = `${typeKey}|${sub.toLowerCase()}`;
      if (!seenPairKeys.has(pairKey)) {
        seenPairKeys.add(pairKey);
        combined.push({ label: `${type}-${sub}`, typeKey });
      }
      typesWithPairs.add(typeKey);
    } else if (!bareTypes.some((b) => b.key === typeKey)) {
      bareTypes.push({ label: type, key: typeKey });
    }
  }

  // Fallback for payloads with no type1..8 data at all: keep the old
  // behaviour of plain category bubbles.
  if (combined.length === 0 && bareTypes.length === 0) {
    return resource.categories || (resource.category ? [resource.category] : []);
  }

  const labels = combined.map((c) => c.label);
  bareTypes.forEach((bare) => {
    if (!typesWithPairs.has(bare.key)) labels.push(bare.label);
  });
  return labels;
};

/**
 * Sub-categories that are hidden from the dropdown in Youth Safe Mode.
 * The backend still enforces blocking via YouthBlock + blocked combinations;
 * this just avoids offering adult-oriented filters in the youth-safe UI.
 */
const ADULT_SUBCATEGORIES = [
  'Erotic',
  'Sex',
  'Fetish',
  'Levi/Leather',
  'Leather',
  'S/M',
  'Naturist',
  'Bears',
  'Cannabis',
  'Dating',
  'Motorcycle',
];

/**
 * Keyed by the widget's dropdown category names (not the API names —
 * e.g. key is 'AIDS/HIV' even though the API category is 'AIDS').
 * Ordered roughly by how common each subtype is in the live data.
 */
const SUBCATEGORIES = {
  'AIDS/HIV': [
    'Testing', 'Support', 'ASO', 'Clinic', 'Medical', 'Education',
    'Counseling', 'Treatment', 'Social', 'Food Pantry', 'Housing',
    'Helpline', 'Meals', 'Shelter',
  ],
  'Bar/Club': [
    'GLBT', 'Gay Men', 'Gay Straight', 'Dance', 'Neighborhood',
    'Cabaret/Piano', 'Leather', 'Lesbian', 'Sports', 'Video', 'Western',
    'Drag', 'Transgender',
  ],
  'Bisexual': [
    'Social', 'Support', 'Dating', 'Internet',
  ],
  'Bookstore': [
    'GLBT', 'Feminist', 'Mail Order', 'Cards/Gifts', 'Hobbies', 'Coffee Shop',
  ],
  'Business': [
    'Fashion', 'Mail Order', 'Cards/Gifts', 'Salons', 'Tattoo',
    'Employee Group', 'Coaching', 'Gyms', 'Travel', 'Marketing/PR',
    'Real Estate', 'Film', 'Internet', 'Jewelry', 'Wedding', 'Pets',
    'Chamber of Commerce', 'Financial', 'Erotic', 'Dating', 'Cannabis',
  ],
  'Community Center': [
    'Support', 'Food Pantry', 'Social', 'Pride', 'Medical', 'Counseling',
    'Testing', 'Education',
  ],
  'Counseling': [
    'Support', 'Bilingual', 'Alcohol', 'Narcotics', 'Coaching',
  ],
  'Crisis': [
    'Food Pantry', 'Support', 'Violence', 'Shelter', 'Counseling', 'Hotline',
    'Education', 'Runaway', 'Suicide', 'Youth Shelter', 'Meals', 'Testing',
  ],
  'Cultural': [
    'Film', 'Theater', 'Archive', 'Dance', 'Arts', 'Chorus', 'Video',
    'Band', 'Activist',
  ],
  'Fundraising': [
    'Support', 'Education', 'Activist', 'Social', 'Theater', 'Scholarships',
  ],
  'Health': [
    'Medical', 'Clinic', 'Transgender', 'Testing', 'Primary Care',
    'Chiropractor', 'Dentist', 'Support', 'Treatment', 'Internal Medicine',
    'Alternative', 'Surgery', 'Adolescent', 'Gynecologist', 'Veterinarian',
    'Counseling',
  ],
  'Hotel': [
    'GLBT', 'Gay Men', 'Campground',
  ],
  'Hotline': [
    'Crisis', 'Support', 'Violence', 'Suicide', 'Helpline', 'Youth', 'GLBT',
  ],
  'Legal': [
    'Family', 'Discrimination', 'Immigration', 'General', 'Will/Estates',
    'Real Estate', 'Police Liason', 'Name Change', 'Divorce', 'Business',
    'Criminal', 'Negligence', 'Referral',
  ],
  'Lesbian': [
    'Social', 'Internet', 'Support', 'Magazine', 'Activist',
  ],
  'Media': [
    'Internet', 'Magazine', 'Publisher', 'Film', 'Video', 'Press', 'Dating',
    'Directory', 'Archive', 'TV/Cable', 'Radio', 'Newsletter',
  ],
  'Political': [
    'Activist', 'Democrat', 'Lobbying',
  ],
  'Recovery': [
    'Alcohol', 'Narcotics', 'Sexual', 'Overeaters',
  ],
  'Religion': [
    'UCC', 'Episcopalian', 'Lutheran', 'Unitarian', 'Methodist',
    'Presbyterian', 'Christian', 'Jewish', 'Baptist', 'Food Pantry',
    'Disciples', 'Spiritual', 'Catholic', 'MCC', 'Anabaptist', 'Seminary',
    'Islam', 'Atheist/Humanist',
  ],
  'Restaurant': [
    'Coffee Shop', 'Gay Straight', 'Catering',
  ],
  'Senior': [
    'Support', 'Social', 'Meals', 'Disability', 'Housing', 'Legal', 'Medical',
  ],
  'Social': [
    'GLBT', 'Pride', 'Gay Men', 'Lesbian', 'Bi', 'Hobbies', 'POC', 'Dance',
    'Asian', 'Latino/a', 'Family', 'Theater', 'Outdoors', 'Dating',
    'Levi/Leather', 'S/M', 'Fetish', 'Motorcycle', 'Naturist', 'Bears',
    'Gaymer', 'Square Dance', 'Spiritual', 'Jewish', 'Bicycling',
  ],
  'Sports': [
    'Running', 'Outdoors', 'Bicycling', 'Bowling', 'Tennis', 'Football',
    'Softball', 'Volleyball', 'Yoga', 'Cheerleading', 'Basketball', 'Rugby',
    'Sailing', 'Swimming', 'Hockey', 'Soccer', 'Golf', 'Rodeo', 'Scuba',
  ],
  'Student': [
    'High School', 'Social', 'Support', 'Resource Center', 'Scholarships',
    'Alumni', 'Academic', 'Under 13', 'K to 8', 'Faculty', 'Fraternity',
  ],
  'Support': [
    'Family', 'GLBT', 'POC', 'Social', 'Bilingual', 'Lesbian', 'Gay Men',
    'Relocation', 'Disability', 'Seniors', 'Latino/a', 'Asian', 'Bi',
    'Asexual', 'Immigration', 'Intersex', 'Military', 'Marriage', 'Prison',
  ],
  'Transgender': [
    'Support', 'Medical', 'Clinic', 'Testing', 'Counseling', 'Social',
    'Youth', 'Family', 'Masculine', 'Feminine', 'Non binary', 'FTM',
    'Political', 'Legal', 'Binder/Breastform', 'Surgery', 'Name Change',
    'Relocation',
  ],
  'Youth': [
    'Support', 'Social', 'High School', 'Counseling', 'Education',
    'Under 13', 'Runaway', 'Food Pantry', 'Shelter', 'Family', 'Crisis',
    'Medical', 'K to 8', 'Camp', 'Youth Shelter', 'Pride',
  ],
};

/**
 * Returns the sub-category list for a category, filtered for Youth Safe Mode
 * if applicable. Returns null when the category has no sub-categories.
 *
 * @param {string} category - The selected dropdown category
 * @param {boolean} youthSafe - Whether Youth Safe Mode is active
 * @returns {string[]|null}
 */
const getSubcategoriesFor = (category, youthSafe = false) => {
  const subs = SUBCATEGORIES[category];
  if (!subs || subs.length === 0) return null;
  if (!youthSafe) return subs;
  const filtered = subs.filter((s) => !ADULT_SUBCATEGORIES.includes(s));
  return filtered.length > 0 ? filtered : null;
};

// =============================================================================
// MAIN COMPONENT
// =============================================================================

/**
 * LGBTResourcesMap Component
 * 
 * Main map component for displaying LGBT+ resources with interactive features.
 * 
 * PROPS:
 * @param {string} apiKey - API key for backend authentication (default: 'demo')
 * @param {string} theme - Color theme: 'purple', 'inverted', 'monochrome', 'ocean'
 * @param {array} defaultCenter - Default map center [lat, lng] (default: center of US)
 * @param {boolean} youthSafe - Enable Youth Safe Mode to filter adult venues
 * 
 * USAGE:
 * ```jsx
 * // Basic usage
 * <LGBTResourcesMap apiKey="your-api-key" />
 * 
 * // Youth Safe Mode (for embedding on youth-focused sites)
 * <LGBTResourcesMap apiKey="your-api-key" youthSafe={true} />
 * 
 * // Custom theme and default location
 * <LGBTResourcesMap 
 *   apiKey="your-api-key" 
 *   theme="ocean"
 *   defaultCenter={[40.7128, -74.0060]} // NYC
 * />
 * ```
 */
const LGBTResourcesMap = ({ apiKey = 'demo', theme = 'purple', defaultCenter = null, youthSafe = false }) => {
  
  // ===========================================================================
  // STATE MANAGEMENT
  // ===========================================================================
  
  // --- Map Display State ---
  const [mapCenter, setMapCenter] = useState(defaultCenter || [39.8283, -98.5795]); // Center of US
  const [mapZoom, setMapZoom] = useState(5);           // Target zoom level
  const [currentZoom, setCurrentZoom] = useState(5);   // Actual current zoom (for indicator)
  const [isMapReady, setIsMapReady] = useState(false); // Controls CSS transitions
  const mapRef = useRef();                              // Reference to Leaflet map instance
  const markerRefs = useRef({});                        // References to marker instances for popup control
  
  // --- Search Results State ---
  const [resources, setResources] = useState([]);      // Array of resource objects
  const [loading, setLoading] = useState(false);       // Loading indicator
  const [error, setError] = useState(null);            // Error message
  const [hasSearched, setHasSearched] = useState(false); // Has user performed a search?
  const [activeResourceId, setActiveResourceId] = useState(null); // Currently selected resource
  
  // --- Search Parameters State ---
  const [includeAdult, setIncludeAdult] = useState(!youthSafe); // Include 18+ venues?
  const [searchParams, setSearchParams] = useState({
    zip: '',
    category: 'All Categories',
    subcategory: ALL_SUBCATEGORIES_LABEL,
    radius: 5 // Default 5 miles - good balance of results vs. relevance
  });

  // --- "Search This Area" Feature State ---
  const [originalSearchCenter, setOriginalSearchCenter] = useState(null);
  const [originalSearchRadius, setOriginalSearchRadius] = useState(null);
  const [showSearchHereButton, setShowSearchHereButton] = useState(false);
  const [currentViewportCenter, setCurrentViewportCenter] = useState(null);
  const [searchingNewArea, setSearchingNewArea] = useState(false);
  const [allowMapController, setAllowMapController] = useState(true);

  // --- iOS Maps Chooser Modal State ---
  const [showMapsModal, setShowMapsModal] = useState(false);
  const [mapsModalData, setMapsModalData] = useState({ address: '', lat: null, lng: null });
  
  // --- Zoom Tracking State ---
  const [ignoreZoomTracking, setIgnoreZoomTracking] = useState(false);

  // --- More Details Dropdown State ---
  const [expandedDetails, setExpandedDetails] = useState({});
  
  const toggleExpandedDetails = (e, resourceId) => {
    e.stopPropagation();
    e.preventDefault();
    setExpandedDetails(prev => ({
      ...prev,
      [resourceId]: !prev[resourceId]
    }));
  };

  // ===========================================================================
  // THEME SETUP
  // ===========================================================================
  
  // Get colors for current theme (fallback to purple if invalid)
  const themeColors = THEME_COLORS[theme] || THEME_COLORS.purple;

  /**
   * Apply theme CSS variables to the container element.
   * This allows CSS to reference theme colors via var(--theme-primary), etc.
   */
  useEffect(() => {
    const container = document.querySelector('.lgbt-map-container');
    if (container) {
      container.style.setProperty('--theme-primary', themeColors.primary);
      container.style.setProperty('--theme-primary-rgb', themeColors.primaryRgb);
      container.style.setProperty('--theme-secondary', themeColors.secondary);
      container.style.setProperty('--theme-secondary-rgb', themeColors.secondaryRgb);
      container.style.setProperty('--theme-accent', themeColors.accent);
      container.style.setProperty('--theme-accent-rgb', themeColors.accentRgb);
      container.style.setProperty('--theme-light', themeColors.light);
      container.style.setProperty('--theme-gradient', themeColors.gradient);
      container.style.setProperty('--theme-gradient-reverse', themeColors.gradientReverse);
    }
  }, [theme, themeColors]);

  // Clear marker refs when resources change
  useEffect(() => {
    markerRefs.current = {};
  }, [resources]);

  // ===========================================================================
  // CATEGORY CONFIGURATION
  // ===========================================================================
  
  /**
   * Available categories for filtering.
   * 
   * Available categories from LGBT Near Me full version.
   * Alphabetically sorted per Aaron's request (Jan 2026).
   * 
   * NOTE: Bar/Club is hidden when youthSafe=true
   */
  const allCategories = [
    'All Categories',
    'AIDS/HIV',
    'Bar/Club',           // HIDDEN IN YOUTH SAFE MODE
    'Bisexual',
    'Bookstore',
    'Business',
    'Community Center',
    'Counseling',
    'Crisis',
    'Crisis - Food Pantry',
    'Cultural',
    'Fundraising',
    'Health',
    'Hotel',
    'Hotline',
    'Legal',
    'Lesbian',
    'Media',
    'Political',
    'Recovery',
    'Religion',
    'Restaurant',
    'Senior',
    'Social',
    'Sports',
    'Student',
    'Support',
    'Transgender',
    'Youth'
  ];
  
  // Filter out adult categories when Youth Safe Mode is active
  const categories = youthSafe 
    ? allCategories.filter(cat => cat !== 'Bar/Club')
    : allCategories;

  // ===========================================================================
  // CATEGORY COLOR MAPPING
  // ===========================================================================
  
  /**
   * Returns the display color for a resource category.
   * 
   * COLOR DESIGN PRINCIPLES:
   * 1. Each category has a unique, visually distinct color
   * 2. RED (#FF3B30) is reserved ONLY for Crisis resources (urgent/emergency)
   * 3. Bar/Club uses AMBER (#E67E22) - distinct from Crisis red
   * 4. Colors are chosen for accessibility and visual harmony
   * 
   * WHY BAR/CLUB ISN'T RED:
   * Previously, adult venues (Bar/Club) were colored red, which caused confusion
   * because users associated red dots with urgent/crisis resources. Now:
   * - Red = Crisis (urgent help needed)
   * - Amber = Bar/Club (adult venues)
   * - Adult venues are additionally marked with an "18+" badge (see createCustomIcon)
   * 
   * @param {string} category - Category name from the API
   * @returns {string} Hex color code
   * 
   * @example
   * getCategoryColor('Crisis')   // Returns '#FF3B30' (red)
   * getCategoryColor('Bar/Club') // Returns '#E67E22' (amber)
   * getCategoryColor('Health')   // Returns '#FF6B9D' (pink)
   */
  const getCategoryColor = (category) => {
    const colors = {
      // =======================================================================
      // MAXIMALLY DISTINCT COLOR PALETTE
      // Colors chosen to be easily distinguishable from each other
      // =======================================================================
      
      // RED (emergency only)
      'Crisis': '#FF3B30',        // Red - EMERGENCY ONLY
      
      // ORANGE/YELLOW  
      'Bar/Club': '#FF9500',      // Orange
      'Business': '#FFD700',      // Gold/Yellow
      
      // GREENS (spread from lime to forest)
      'Youth': '#ADFF2F',         // Yellow-Green/Lime
      'Sports': '#228B22',        // Forest Green
      
      // TEAL/CYAN
      'Community': '#008080',     // Teal
      'Social': '#00CED1',        // Turquoise
      
      // BLUES (spread from light to dark)
      'Student': '#1E90FF',       // Dodger Blue  
      'Legal': '#0000CD',         // Medium Blue
      
      // PURPLES
      'Cultural': '#9400D3',      // Dark Violet
      'Religious': '#663399',     // Rebecca Purple
      'Religion': '#663399',      // (API alias)
      
      // PINKS/MAGENTAS (spread across spectrum)
      'Transgender': '#FF1493',   // Deep Pink
      'Health': '#FA8072',        // Salmon/Coral
      'Support': '#FF00FF',       // Magenta
      
      // NEUTRAL
      'Senior': '#8B4513',        // Saddle Brown
      'Lesbian': '#E91E8C',       // Hot Pink
      'Restaurant': '#FF7043',    // Deep Orange
      
      // =======================================================================
      // LEGACY/ALIAS TYPES
      // =======================================================================
      'AIDS': '#FA8072',          // Same as Health
      'Counseling': '#FF00FF',    // Same as Support
      'Recovery': '#FA8072',      // Same as Health
      'Community Center': '#008080', // Same as Community
    };
    
    // Fallback to theme secondary color for unknown categories
    return colors[category] || themeColors.secondary;
  };

  // ===========================================================================
  // EVENT HANDLERS
  // ===========================================================================

  /**
   * Handles the "Get Directions" button click.
   * 
   * Behavior varies by platform:
   * - iOS: Shows modal to choose between Apple Maps and Google Maps
   * - Android: Opens system "Open with" dialog via geo: URL
   * - Desktop: Opens Google Maps in new browser tab
   */
  const handleDirectionsClick = (e, address, lat, lng) => {
    e.stopPropagation();
    e.preventDefault();
    
    const platform = getPlatform();
    
    if (platform === 'ios') {
      setMapsModalData({ address, lat, lng });
      setShowMapsModal(true);
    } else if (platform === 'android') {
      window.location.href = getDirectionsUrl(address, lat, lng, 'android');
    } else {
      window.open(getDirectionsUrl(address, lat, lng, 'desktop'), '_blank');
    }
  };

  /**
   * Handles selection from the iOS maps modal.
   */
  const handleMapsModalSelect = (mapType) => {
    const { address, lat, lng } = mapsModalData;
    const url = getDirectionsUrl(address, lat, lng, mapType);
    window.location.href = url;
    setShowMapsModal(false);
  };

  // ===========================================================================
  // MARKER ICON CREATION
  // ===========================================================================

  /**
   * Creates a custom Leaflet DivIcon marker for map display.
   * 
   * MARKER DESIGN:
   * - Circular dot with white border (matches Apple Maps style)
   * - Color based on category (NOT age restriction)
   * - Active/selected markers are larger with pulse animation
   * - Adult venues (18+) show a small red badge in the corner
   * 
   * WHY CATEGORY COLOR + BADGE (not red for adult):
   * Previously, all adult venues were colored red, which:
   * 1. Made them look like Crisis/emergency resources
   * 2. Lost the category information (is it a bar? social group? business?)
   * 
   * Now, adult venues keep their category color (so you can see it's a Bar/Club
   * or Social group) but have an "18+" badge to indicate age restriction.
   * 
   * @param {boolean} isAgeRestricted - Whether resource is 18+ (bars, clubs, etc.)
   * @param {boolean} isActive - Whether marker is currently selected
   * @param {string} name - Resource name (shown in tooltip)
   * @param {string} category - Resource category (determines color)
   * @returns {L.DivIcon} Leaflet DivIcon for use with Marker
   */
  const createCustomIcon = (isAgeRestricted = false, isActive = false, name = '', category = '') => {
    // Always use category color - adult venues show their category, NOT red
    const color = getCategoryColor(category);
    const size = isActive ? 18 : 14;
    const tooltipText = name;
    const tooltipCategory = category;
    
    // Adult venue indicator - red ring around the marker
    const adultRing = isAgeRestricted ? `
      <div class="marker-adult-ring" style="
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        width: ${size + 4}px;
        height: ${size + 4}px;
        border: 2px solid #FF3B30;
        border-radius: 50%;
        pointer-events: none;
      "></div>
    ` : '';
    
    return L.divIcon({
      html: `
        <div class="apple-marker-simple" style="position: relative; width: ${size + 10}px; height: ${size + 10}px;">
          ${adultRing}
          <div class="marker-circle ${isActive ? 'active' : ''}" style="position: absolute; top: 50%; left: 50%; transform: translate(-50%, -50%); width: ${size}px; height: ${size}px; background: ${color}; border: 3px solid white; border-radius: 50%; box-shadow: 0 1px 3px rgba(0,0,0,0.4); cursor: pointer; transition: transform 0.2s;">
          </div>
          ${isActive ? `<div class="marker-pulse" style="position: absolute; top: 50%; left: 50%; transform: translate(-50%, -50%); width: ${size + 6}px; height: ${size + 6}px; border-radius: 50%; border: 2px solid ${color}; animation: markerPulse 2s ease-out infinite; pointer-events: none;"></div>` : ''}
          <div class="marker-tooltip" style="background: var(--theme-gradient, linear-gradient(135deg, #491994 0%, #5f2bb8 100%)); border-color: rgba(var(--theme-accent-rgb, 175, 139, 239), 0.6);">
            <div class="tooltip-name">${tooltipText}</div>
            ${tooltipCategory ? `<div class="tooltip-category">${tooltipCategory}</div>` : ''}
          </div>
        </div>
      `,
      className: 'custom-apple-marker',
      iconSize: [size + 10, size + 10],
      iconAnchor: [(size + 10)/2, (size + 10)/2],
      popupAnchor: [0, -(size + 10)/2]
    });
  };

  // ===========================================================================
  // GEOCODING FUNCTIONS
  // ===========================================================================

  /**
   * Converts a US ZIP or Canadian postal code to lat/lng coordinates
   * using OpenStreetMap Nominatim.
   */
  const geocodeZipCode = async (zip) => {
    try {
      const country = getPostalCountry(zip) || 'US';
      const normalized = normalizePostalCode(zip);
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?postalcode=${encodeURIComponent(normalized)}&country=${country}&format=json&limit=1`
      );
      const data = await response.json();
      if (data && data.length > 0) {
        return {
          lat: parseFloat(data[0].lat),
          lng: parseFloat(data[0].lon)
        };
      }
      return null;
    } catch (error) {
      console.error('Geocoding error:', error);
      return null;
    }
  };

  /**
   * Reverse geocodes lat/lng coordinates to a ZIP code.
   */
  const reverseGeocodeToZip = async (lat, lng) => {
    try {
      const response = await fetch(`https://nominatim.openstreetmap.org/reverse?lat=${lat}&lon=${lng}&format=json`);
      const data = await response.json();
      if (data && data.address && data.address.postcode) {
        const zip = data.address.postcode.split('-')[0];
        return zip;
      }
      return null;
    } catch (error) {
      console.error('Reverse geocoding error:', error);
      return null;
    }
  };

  // ===========================================================================
  // MAIN SEARCH FUNCTION
  // ===========================================================================

  /**
   * Performs a resource search based on current search parameters.
   */
  const handleSearch = async () => {
    if (!isValidPostalCode(searchParams.zip)) {
      setError('Please enter a valid ZIP code (12345) or Canadian postal code (A1A 1A1)');
      return;
    }

    const normalizedZip = normalizePostalCode(searchParams.zip);

    setLoading(true);
    setError(null);
    setIsMapReady(false);
    setAllowMapController(true);

    try {
      const minLoadTime = new Promise(resolve => setTimeout(resolve, 500));
      
      const zipCenter = await geocodeZipCode(normalizedZip);
      if (zipCenter) {
        setMapCenter([zipCenter.lat, zipCenter.lng]);
        setOriginalSearchCenter([zipCenter.lat, zipCenter.lng]);
        setOriginalSearchRadius(searchParams.radius);
        
        // Calculate zoom level based on radius
        let zoomLevel;
        if (searchParams.radius <= 1) zoomLevel = 17;
        else if (searchParams.radius <= 5) zoomLevel = 15;
        else if (searchParams.radius <= 10) zoomLevel = 13;
        else if (searchParams.radius <= 25) zoomLevel = 12;
        else if (searchParams.radius <= 50) zoomLevel = 9;
        else zoomLevel = 7;
        
        setMapZoom(zoomLevel);
        setCurrentZoom(zoomLevel);
        setIgnoreZoomTracking(true);
        setTimeout(() => setIgnoreZoomTracking(false), 2000);
      }

      const apiUrl = `${API_BASE_URL}/api/resources`;
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-API-Key': apiKey
        },
        body: JSON.stringify({
          zip: normalizedZip,
          radius: searchParams.radius,
          category: searchParams.category,
          subcategory: searchParams.subcategory !== ALL_SUBCATEGORIES_LABEL ? searchParams.subcategory : null,
          includeAdult: includeAdult,
          apiKey: apiKey
        })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `Server error (${response.status})`);
      }

      const data = await response.json();
      await minLoadTime;
      
      if (data.resources && data.resources.length > 0) {
        // Process and deduplicate resources
        const resourcesWithCoords = data.resources
          .map((resource, index) => {
            const lat = parseFloat(resource.latitude);
            const lng = parseFloat(resource.longitude);
            if (isNaN(lat) || isNaN(lng) || lat === 0 || lng === 0) return null;
            return {
              ...resource,
              id: resource.id || resource.idnum || index,
              coordinates: { lat, lng }
            };
          })
          .filter(resource => resource !== null);
        
        // Deduplicate by name+address and merge categories
        const uniqueResourcesMap = new Map();
        resourcesWithCoords.forEach(resource => {
          const key = `${resource.name}-${resource.address || resource.coordinates.lat + ',' + resource.coordinates.lng}`;
          if (uniqueResourcesMap.has(key)) {
            const existing = uniqueResourcesMap.get(key);
            // Merge categories from duplicate entries
            const existingCats = existing.categories || (existing.category ? [existing.category] : []);
            const newCats = resource.categories || (resource.category ? [resource.category] : []);
            const mergedCats = [...new Set([...existingCats, ...newCats])];
            existing.categories = mergedCats.length > 0 ? mergedCats : null;
            
            // Merge type/subtype pairs too (added 5 Aug 2026): badges are
            // built from `types` via buildCategoryBadgeLabels, so without
            // this a merged listing would silently lose the second entry's
            // badges. Pairs are deduped case-insensitively; first 8 kept.
            const pairKeyOf = (t, s) => `${(t || '').trim().toLowerCase()}|${(s || '').trim().toLowerCase()}`;
            const collectPairs = (typesObj, seen, out) => {
              if (!typesObj) return;
              for (let i = 1; i <= 8; i++) {
                const t = typeof typesObj[`type${i}`] === 'string' ? typesObj[`type${i}`].trim() : '';
                if (!t) continue;
                const s = typeof typesObj[`subtype${i}`] === 'string' ? typesObj[`subtype${i}`].trim() : '';
                const k = pairKeyOf(t, s);
                if (!seen.has(k)) {
                  seen.add(k);
                  out.push({ t, s });
                }
              }
            };
            const seenPairs = new Set();
            const mergedPairs = [];
            collectPairs(existing.types, seenPairs, mergedPairs);
            collectPairs(resource.types, seenPairs, mergedPairs);
            if (mergedPairs.length > 0) {
              const mergedTypes = {};
              mergedPairs.slice(0, 8).forEach((p, idx) => {
                mergedTypes[`type${idx + 1}`] = p.t;
                mergedTypes[`subtype${idx + 1}`] = p.s;
              });
              existing.types = mergedTypes;
            }
          } else {
            uniqueResourcesMap.set(key, { ...resource });
          }
        });
        
        setResources(Array.from(uniqueResourcesMap.values()));
        setHasSearched(true);
        setTimeout(() => setIsMapReady(true), 100);
      } else {
        setError(null);
        setResources([]);
        setHasSearched(true);
        setIsMapReady(true);
      }
    } catch (err) {
      console.error('Search error:', err);
      setError(err.message || 'Unable to load resources. Please try again later.');
      setResources([]);
      setIsMapReady(true);
    } finally {
      setLoading(false);
    }
  };

  // ===========================================================================
  // "SEARCH THIS AREA" FEATURE
  // ===========================================================================

  /**
   * Handles the "Search This Area" button click.
   */
  const handleSearchThisArea = async () => {
    if (!currentViewportCenter) return;

    setSearchingNewArea(true);
    setShowSearchHereButton(false);
    setAllowMapController(false);

    try {
      const zip = await reverseGeocodeToZip(currentViewportCenter[0], currentViewportCenter[1]);
      
      if (!zip) {
        setError('Unable to determine location. Please try again.');
        setSearchingNewArea(false);
        setAllowMapController(true);
        return;
      }

      const radiusInfo = getRadiusFromZoom(currentZoom);
      const searchRadius = radiusInfo.miles;

      setMapCenter(currentViewportCenter);
      setOriginalSearchCenter(currentViewportCenter);
      setOriginalSearchRadius(searchRadius);

      const apiUrl = `${API_BASE_URL}/api/resources`;
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-API-Key': apiKey
        },
        body: JSON.stringify({
          zip: zip,
          radius: searchRadius,
          category: searchParams.category,
          subcategory: searchParams.subcategory !== ALL_SUBCATEGORIES_LABEL ? searchParams.subcategory : null,
          includeAdult: includeAdult,
          apiKey: apiKey
        })
      });

      if (!response.ok) throw new Error('Failed to search this area');

      const data = await response.json();

      if (data.resources && data.resources.length > 0) {
        const newResources = data.resources
          .filter(resource => resource.latitude && resource.longitude)
          .map(resource => ({
            ...resource,
            coordinates: {
              lat: parseFloat(resource.latitude),
              lng: parseFloat(resource.longitude)
            }
          }));

        // Add new resources to existing ones (don't replace)
        setResources(prevResources => {
          const existingIds = new Set(prevResources.map(r => r.id));
          const uniqueNewResources = newResources.filter(r => !existingIds.has(r.id));
          return [...prevResources, ...uniqueNewResources];
        });
      }
    } catch (err) {
      console.error('Search this area error:', err);
    } finally {
      setSearchingNewArea(false);
    }
  };

  /**
   * Handles map movement events from MapMovementTracker.
   */
  const handleMapMove = (moveData) => {
    const { center, isOutsideRadius } = moveData;
    setCurrentViewportCenter(center);
    setShowSearchHereButton(isOutsideRadius && !searchingNewArea);
  };

  /**
   * Handles zoom changes from MapMovementTracker.
   */
  const handleZoomChange = (zoom) => {
    if (!ignoreZoomTracking) setCurrentZoom(zoom);
  };

  // ===========================================================================
  // FORM HANDLERS
  // ===========================================================================

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setSearchParams(prev => ({
      ...prev,
      [name]: name === 'radius' ? parseInt(value) : value,
      // Changing the parent category resets the subcategory
      ...(name === 'category' ? { subcategory: ALL_SUBCATEGORIES_LABEL } : {})
    }));
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !loading) handleSearch();
  };

  const handleResourceClick = (resourceId) => {
    setActiveResourceId(resourceId);
    const resource = resources.find(r => r.id === resourceId);
    if (resource && mapRef.current) {
      const currentZoom = mapRef.current.getZoom();
      const targetZoom = Math.max(currentZoom, 14); // Don't zoom out, only in if needed
      
      mapRef.current.flyTo([resource.coordinates.lat, resource.coordinates.lng], targetZoom, {
        animate: true,
        duration: 1.2,
        easeLinearity: 0.25
      });
      
      // Open the popup for this marker after the map finishes moving
      setTimeout(() => {
        const marker = markerRefs.current[resourceId];
        if (marker) {
          marker.openPopup();
        }
      }, 300);
    }
    
    // Scroll to the resource in the results list
    setTimeout(() => {
      const cardElement = document.querySelector(`[data-resource-id="${resourceId}"]`);
      if (cardElement) {
        cardElement.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }
    }, 100);
  };

  const currentZoomInfo = getRadiusFromZoom(currentZoom);

  // ===========================================================================
  // RENDER
  // ===========================================================================

  return (
    <div 
      className={`lgbt-map-container theme-${theme}`} 
      data-theme={theme}
      style={{
        '--theme-primary': themeColors.primary,
        '--theme-primary-rgb': themeColors.primaryRgb,
        '--theme-secondary': themeColors.secondary,
        '--theme-secondary-rgb': themeColors.secondaryRgb,
        '--theme-accent': themeColors.accent,
        '--theme-accent-rgb': themeColors.accentRgb,
        '--theme-light': themeColors.light,
        '--theme-gradient': themeColors.gradient,
        '--theme-gradient-reverse': themeColors.gradientReverse
      }}
    >
      <div className="lgbt-map-wrapper">
        {/* Search Panel */}
        <div className={`lgbt-map-search-panel ${loading ? 'loading' : ''}`}>
          <div className="lgbt-map-search-header">
            <div className="lgbt-map-search-info">
              <h3>Find LGBT+ Resources</h3>
              {hasSearched && resources.length > 0 && !loading && (
                <span className="lgbt-map-search-summary">
                  Found {resources.length} resources in {searchParams.zip}
                </span>
              )}
            </div>
            <div className="lgbt-map-brand">
              <h4 className="lgbt-map-brand-title">lgbt near me</h4>
              <p className="lgbt-map-brand-tagline">the national lgbtq+ community database</p>
              <p className="lgbt-map-brand-powered-by">
                powered by the{' '}
                <a 
                  href="https://www.lgbthotline.org/" 
                  target="_blank" 
                  rel="noopener noreferrer"
                >
                  LGBT National Help Center
                </a>
              </p>
              <p className="lgbt-map-brand-hotline">
                lgbt national hotline: 888-843-4564
              </p>
            </div>
          </div>

          <div className="lgbt-map-search-form">
            <input
              type="text"
              name="zip"
              placeholder="Enter ZIP/Postal Code..."
              maxLength="7"
              value={searchParams.zip}
              onChange={handleInputChange}
              onKeyPress={handleKeyPress}
              className="lgbt-map-input"
              disabled={loading}
            />
            
            <select
              name="category"
              value={searchParams.category}
              onChange={handleInputChange}
              className="lgbt-map-select"
              disabled={loading}
            >
              {categories.map(cat => (
                <option key={cat} value={cat}>{cat}</option>
              ))}
            </select>

            {/* Sub-category dropdown — only shown when the selected
                category has sub-categories in Tim's data */}
            {SHOW_SUBCATEGORY_SEARCH && (() => {
              const subs = getSubcategoriesFor(searchParams.category, youthSafe || !includeAdult);
              if (!subs) return null;
              return (
                <select
                  name="subcategory"
                  value={searchParams.subcategory}
                  onChange={handleInputChange}
                  className="lgbt-map-select lgbt-map-subcategory-select"
                  disabled={loading}
                  aria-label="Subcategory"
                >
                  <option value={ALL_SUBCATEGORIES_LABEL}>{ALL_SUBCATEGORIES_LABEL}</option>
                  {subs.map(sub => (
                    <option key={sub} value={sub}>{sub}</option>
                  ))}
                </select>
              );
            })()}

            <select
              name="radius"
              value={searchParams.radius}
              onChange={handleInputChange}
              className="lgbt-map-select"
              disabled={loading}
            >
              <option value={1}>1 mile</option>
              <option value={5}>5 miles</option>
              <option value={10}>10 miles</option>
              <option value={25}>25 miles</option>
              <option value={50}>50 miles</option>
              <option value={100}>100 miles</option>
            </select>

            <button
              onClick={handleSearch}
              disabled={loading}
              className="lgbt-map-search-btn"
            >
              {loading ? 'Searching...' : 'Search'}
            </button>

            {/* Adult content toggle - hidden in Youth Safe mode */}
            {!youthSafe && (
              <div className="lgbt-map-checkbox-group">
                <label className="lgbt-map-checkbox-label">
                  <input
                    type="checkbox"
                    checked={includeAdult}
                    onChange={(e) => setIncludeAdult(e.target.checked)}
                    disabled={loading}
                  />
                  <span>Include 18+ venues (bars, clubs)</span>
                </label>
              </div>
            )}

            {youthSafe && (
              <div className="lgbt-map-youth-badge">
                🛡️ Youth Safe Mode Active
              </div>
            )}

            {error && !loading && (
              <div className="lgbt-map-error">{error}</div>
            )}
          </div>
        </div>

        {/* Map Content */}
        <div className="lgbt-map-content">
          <div className={`lgbt-map-view ${isMapReady ? 'ready' : ''}`}>
            {loading && <LoadingAnimation themeColors={themeColors} />}
            
            <MapContainer
              ref={mapRef}
              center={mapCenter}
              zoom={mapZoom}
              style={{ height: '100%', width: '100%' }}
              className="lgbt-map-leaflet"
              scrollWheelZoom={true}
              zoomAnimation={true}
              fadeAnimation={true}
              markerZoomAnimation={true}
              preferCanvas={true}
            >
              <TileLayer
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions">CARTO</a>'
                url="https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png"
                maxZoom={20}
                minZoom={3}
                updateWhenIdle={true}
                updateWhenZooming={false}
                keepBuffer={6}
                maxNativeZoom={20}
                subdomains={['a', 'b', 'c', 'd']}
              />
              
              <MapController 
                center={mapCenter} 
                zoom={mapZoom} 
                resources={resources} 
                searchRadius={searchParams.radius}
                enabled={allowMapController}
                key={`map-ctrl-${mapZoom}-${searchParams.radius}`}
              />
              
              <MapMovementTracker
                originalCenter={originalSearchCenter}
                originalRadius={originalSearchRadius}
                onMapMove={handleMapMove}
                onZoomChange={handleZoomChange}
                hasSearched={hasSearched}
              />
              
              {/* Resource Markers */}
              {resources.map((resource) => (
                <Marker
                  key={resource.id}
                  ref={(ref) => { if (ref) markerRefs.current[resource.id] = ref; }}
                  position={[resource.coordinates.lat, resource.coordinates.lng]}
                  icon={createCustomIcon(resource.ageRestricted, activeResourceId === resource.id, resource.name, resource.category || '')}
                  eventHandlers={{ click: () => handleResourceClick(resource.id) }}
                >
                  <Popup 
                    className="lgbt-map-popup"
                    maxWidth={280}
                    minWidth={260}
                    closeButton={true}
                    autoClose={true}
                    closeOnClick={false}
                    autoPan={true}
                    autoPanPadding={[50, 50]}
                  >
                    <div className="popup-content">
                      {/* Header row: name left, 18+ right — matches the list
                          cards; keeps the badge off the category line */}
                      <div className="popup-header">
                        <h4>{resource.name}</h4>
                        {resource.ageRestricted && <span className="age-badge">18+</span>}
                      </div>
                      {resource.name2 && <p className="popup-name2">{resource.name2}</p>}
                      {(() => {
                        const badgeLabels = buildCategoryBadgeLabels(resource);
                        if (badgeLabels.length === 0) return null;
                        return (
                          <div className="popup-category-badges">
                            {badgeLabels.map((label, idx) => (
                              <span key={idx} className="popup-category-badge">{label}</span>
                            ))}
                          </div>
                        );
                      })()}
                      {resource.description && <p className="popup-description">{resource.description}</p>}
                      {resource.address && <p className="popup-address">📍 {resource.address}</p>}
                      {resource.phone && (
                        <p className="popup-phone">📞 <a href={`tel:${resource.phone}`}>{resource.phone}</a></p>
                      )}
                      {resource.hotline && (
                        <p className="popup-phone">☎️ <a href={`tel:${resource.hotline}`}>{resource.hotline} (Toll-Free)</a></p>
                      )}
                      {resource.website && (
                        <p className="popup-website">
                          🌐 <a href={resource.website.match(/^https?:\/\//i) ? resource.website : `https://${resource.website}`} target="_blank" rel="noopener noreferrer">Visit Website</a>
                        </p>
                      )}
                      {(resource.website2 || resource.website3 || resource.note) && (
                        <div className="popup-more-details">
                          <button 
                            className="more-details-toggle"
                            onClick={(e) => toggleExpandedDetails(e, `popup-${resource.id}`)}
                          >
                            <span className="more-details-icon">
                              {expandedDetails[`popup-${resource.id}`] ? '▾' : '▸'}
                            </span>
                            <span>More Details</span>
                          </button>
                          {expandedDetails[`popup-${resource.id}`] && (
                            <div className="more-details-content">
                              {resource.website2 && (() => {
                                const platform = getPlatformFromUrl(resource.website2);
                                return (
                                  <p className="popup-website popup-social">
                                    <PlatformIcon platform={platform} /> <a href={resource.website2.match(/^https?:\/\//i) ? resource.website2 : `https://${resource.website2}`} target="_blank" rel="noopener noreferrer">{getPlatformLabel(platform)}</a>
                                  </p>
                                );
                              })()}
                              {resource.website3 && (() => {
                                const platform = getPlatformFromUrl(resource.website3);
                                return (
                                  <p className="popup-website popup-social">
                                    <PlatformIcon platform={platform} /> <a href={resource.website3.match(/^https?:\/\//i) ? resource.website3 : `https://${resource.website3}`} target="_blank" rel="noopener noreferrer">{getPlatformLabel(platform)}</a>
                                  </p>
                                );
                              })()}
                              {resource.note && (
                                <div className="popup-note">
                                  {resource.note.split('\n').map((line, i) => (
                                    <span key={i}>{line}{i < resource.note.split('\n').length - 1 && <br />}</span>
                                  ))}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      )}
                      {resource.distance !== undefined && (
                        <p className="popup-distance">📏 {resource.distance} miles away</p>
                      )}
                      {resource.address && (
                        <button 
                          className="directions-button"
                          onClick={(e) => handleDirectionsClick(e, resource.address, resource.coordinates?.lat, resource.coordinates?.lng)}
                        >
                          🧭 Get Directions
                        </button>
                      )}
                    </div>
                  </Popup>
                </Marker>
              ))}
            </MapContainer>

            {/* Zoom Indicator */}
            {hasSearched && (
              <div className="lgbt-map-zoom-indicator">
                <span className="zoom-level">Level {currentZoomInfo.level}</span>
                <span className="zoom-separator">|</span>
                <span className="zoom-radius">{currentZoomInfo.miles} mi</span>
              </div>
            )}

            {/* Search This Area Button */}
            {showSearchHereButton && !searchingNewArea && (
              <button className="lgbt-map-search-here-button" onClick={handleSearchThisArea}>
                🔍 Search This Area
              </button>
            )}

            {searchingNewArea && (
              <div className="lgbt-map-search-here-loading">Searching...</div>
            )}

            {/* Welcome Message */}
            {!hasSearched && !loading && (
              <div className="lgbt-map-welcome">
                <div className="welcome-content">
                  <h2>Welcome to LGBT+ Resources Map</h2>
                  <p>Enter a ZIP code above to find resources in any area</p>
                  <div className="welcome-features">
                    <span>🗺️ Interactive markers</span>
                    <span>🔍 Filter by category</span>
                    <span>📍 Adjustable radius</span>
                  </div>
                </div>
              </div>
            )}

            {/* Category Legend - Alphabetical Order */}
            {hasSearched && !loading && (
              <div className="category-legend">
                <div className="legend-title">🎨 Categories</div>
                {/* Bar/Club - only shown in adult mode */}
                {!youthSafe && (
                  <div className="legend-item">
                    <div className="legend-dot" style={{ background: '#FF9500' }}></div>
                    <div className="legend-label">Bar/Club</div>
                  </div>
                )}
                <div className="legend-item">
                  <div className="legend-dot" style={{ background: '#FFD700' }}></div>
                  <div className="legend-label">Business</div>
                </div>
                <div className="legend-item">
                  <div className="legend-dot" style={{ background: '#008080' }}></div>
                  <div className="legend-label">Community</div>
                </div>
                {/* Crisis - RED is reserved for urgent resources only */}
                <div className="legend-item">
                  <div className="legend-dot" style={{ background: '#FF3B30' }}></div>
                  <div className="legend-label">Crisis</div>
                </div>
                <div className="legend-item">
                  <div className="legend-dot" style={{ background: '#9400D3' }}></div>
                  <div className="legend-label">Cultural</div>
                </div>
                <div className="legend-item">
                  <div className="legend-dot" style={{ background: '#FA8072' }}></div>
                  <div className="legend-label">Health</div>
                </div>
                <div className="legend-item">
                  <div className="legend-dot" style={{ background: '#0000CD' }}></div>
                  <div className="legend-label">Legal</div>
                </div>
                <div className="legend-item">
                  <div className="legend-dot" style={{ background: '#663399' }}></div>
                  <div className="legend-label">Religious</div>
                </div>
                <div className="legend-item">
                  <div className="legend-dot" style={{ background: '#8B4513' }}></div>
                  <div className="legend-label">Senior</div>
                </div>
                <div className="legend-item">
                  <div className="legend-dot" style={{ background: '#00CED1' }}></div>
                  <div className="legend-label">Social</div>
                </div>
                <div className="legend-item">
                  <div className="legend-dot" style={{ background: '#228B22' }}></div>
                  <div className="legend-label">Sports</div>
                </div>
                <div className="legend-item">
                  <div className="legend-dot" style={{ background: '#1E90FF' }}></div>
                  <div className="legend-label">Student</div>
                </div>
                <div className="legend-item">
                  <div className="legend-dot" style={{ background: '#FF00FF' }}></div>
                  <div className="legend-label">Support</div>
                </div>
                <div className="legend-item">
                  <div className="legend-dot" style={{ background: '#FF1493' }}></div>
                  <div className="legend-label">Transgender</div>
                </div>
                <div className="legend-item">
                  <div className="legend-dot" style={{ background: '#ADFF2F' }}></div>
                  <div className="legend-label">Youth</div>
                </div>
                {/* 18+ ring indicator - shown at end in adult mode */}
                {!youthSafe && (
                  <div className="legend-item legend-18plus">
                    <div style={{ 
                      width: '12px',
                      height: '12px',
                      border: '2px solid #FF3B30',
                      borderRadius: '50%',
                      marginRight: '8px'
                    }}></div>
                    <div className="legend-label">Adult Venue</div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Results Panel */}
          {hasSearched && !loading && (
            <div className={`lgbt-map-results-panel ${isMapReady ? 'ready' : ''}`}>
              <div className="lgbt-map-results-header">
                <h3>{resources.length} Results</h3>
              </div>
              
              {resources.length > 0 ? (
                <>
                  <div className="lgbt-map-results-list">
                    {resources.map(resource => (
                      <div 
                        key={resource.id}
                        data-resource-id={resource.id}
                        className={`lgbt-map-result-card ${activeResourceId === resource.id ? 'active' : ''}`}
                        onClick={() => handleResourceClick(resource.id)}
                      >
                        <div className="result-header">
                          <h4>{resource.name}</h4>
                          {resource.ageRestricted && <span className="age-indicator">18+</span>}
                        </div>
                        {resource.name2 && <p className="result-name2">{resource.name2}</p>}
                        
                        {(() => {
                          const badgeLabels = buildCategoryBadgeLabels(resource);
                          if (badgeLabels.length === 0) return null;
                          return (
                            <div className="result-categories">
                              {badgeLabels.map((label, idx) => (
                                <span key={idx} className="result-category">{label}</span>
                              ))}
                            </div>
                          );
                        })()}
                        
                        {resource.description && <p className="result-description">{resource.description}</p>}
                        
                        <div className="result-details">
                          {resource.address && (
                            <div className="result-detail">
                              <span className="detail-icon">📍</span>
                              <span className="detail-text">{resource.address}</span>
                            </div>
                          )}
                          {resource.phone && (
                            <div className="result-detail">
                              <span className="detail-icon">📞</span>
                              <a href={`tel:${resource.phone}`} onClick={(e) => e.stopPropagation()} className="detail-link">
                                {resource.phone}
                              </a>
                            </div>
                          )}
                          {resource.hotline && (
                            <div className="result-detail">
                              <span className="detail-icon">☎️</span>
                              <a href={`tel:${resource.hotline}`} onClick={(e) => e.stopPropagation()} className="detail-link">
                                {resource.hotline} (Toll-Free)
                              </a>
                            </div>
                          )}
                          {resource.website && (
                            <div className="result-detail">
                              <span className="detail-icon">🌐</span>
                              <a href={resource.website.match(/^https?:\/\//i) ? resource.website : `https://${resource.website}`} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()} className="detail-link">
                                Website
                              </a>
                            </div>
                          )}
                          {(resource.website2 || resource.website3 || resource.note) && (
                            <div className="resource-more-details">
                              <button 
                                className="more-details-toggle"
                                onClick={(e) => toggleExpandedDetails(e, `sidebar-${resource.id}`)}
                              >
                                <span className="more-details-icon">
                                  {expandedDetails[`sidebar-${resource.id}`] ? '▾' : '▸'}
                                </span>
                                <span>More Details</span>
                              </button>
                              {expandedDetails[`sidebar-${resource.id}`] && (
                                <div className="more-details-content">
                                  {resource.website2 && (() => {
                                    const platform = getPlatformFromUrl(resource.website2);
                                    return (
                                      <div className="result-detail">
                                        <span className="detail-icon"><PlatformIcon platform={platform} /></span>
                                        <a href={resource.website2.match(/^https?:\/\//i) ? resource.website2 : `https://${resource.website2}`} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()} className="detail-link">
                                          {getPlatformLabel(platform)}
                                        </a>
                                      </div>
                                    );
                                  })()}
                                  {resource.website3 && (() => {
                                    const platform = getPlatformFromUrl(resource.website3);
                                    return (
                                      <div className="result-detail">
                                        <span className="detail-icon"><PlatformIcon platform={platform} /></span>
                                        <a href={resource.website3.match(/^https?:\/\//i) ? resource.website3 : `https://${resource.website3}`} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()} className="detail-link">
                                          {getPlatformLabel(platform)}
                                        </a>
                                      </div>
                                    );
                                  })()}
                                  {resource.note && (
                                    <div className="resource-note">
                                      <span className="note-icon">📋</span>
                                      <div className="note-text">
                                        {resource.note.split('\n').map((line, i) => (
                                          <span key={i}>{line}{i < resource.note.split('\n').length - 1 && <br />}</span>
                                        ))}
                                      </div>
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                        
                        {resource.distance !== undefined && (
                          <span className="result-distance">{resource.distance} miles away</span>
                        )}
                        
                        {resource.address && (
                          <button 
                            className="directions-button"
                            onClick={(e) => handleDirectionsClick(e, resource.address, resource.coordinates?.lat, resource.coordinates?.lng)}
                          >
                            🧭 Get Directions
                          </button>
                        )}
                      </div>
                    ))}
                  </div>

                  <div className="lgbt-map-powered-by">
                    <a 
                      href="https://www.lgbtnearme.org/add-resource" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="powered-by-missing"
                    >
                      💡 Missing a resource? Let us know →
                    </a>
                  </div>
                </>
              ) : (
                <div className="lgbt-map-no-results">
                  <div className="no-results-icon">🔍</div>
                  <p className="no-results-title">No resources found</p>
                  {(() => {
                    const hasCategory = searchParams.category !== 'All Categories';
                    const hasSubcategory = searchParams.subcategory && searchParams.subcategory !== ALL_SUBCATEGORIES_LABEL;
                    
                    // Name exactly what came up empty so the user knows it's
                    // their specific filter combination, not a broken search
                    let detail = null;
                    if (hasCategory && hasSubcategory) {
                      detail = `No "${searchParams.subcategory}" resources under "${searchParams.category}" within ${searchParams.radius} ${searchParams.radius === 1 ? 'mile' : 'miles'} of ${searchParams.zip}`;
                    } else if (hasCategory) {
                      detail = `No "${searchParams.category}" resources within ${searchParams.radius} ${searchParams.radius === 1 ? 'mile' : 'miles'} of ${searchParams.zip}`;
                    } else {
                      detail = `No resources within ${searchParams.radius} ${searchParams.radius === 1 ? 'mile' : 'miles'} of ${searchParams.zip}`;
                    }
                    
                    return (
                      <>
                        <p className="no-results-detail">{detail}</p>
                        <div className="no-results-suggestions">
                          <p className="no-results-hint">Try:</p>
                          <ul>
                            <li>Expanding your search radius</li>
                            {hasSubcategory && <li>Selecting "All Subcategories"</li>}
                            {hasCategory && <li>Selecting "All Categories"</li>}
                            <li>Trying a nearby ZIP code</li>
                          </ul>
                        </div>
                      </>
                    );
                  })()}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* iOS Maps Chooser Modal */}
      {showMapsModal && (
        <div className="maps-modal-overlay" onClick={() => setShowMapsModal(false)}>
          <div className="maps-modal" onClick={(e) => e.stopPropagation()}>
            <h3>Open directions in:</h3>
            <div className="maps-modal-options">
              <button className="maps-modal-option" onClick={() => handleMapsModalSelect('apple')}>
                <span className="maps-modal-icon">🗺️</span>
                <span>Apple Maps</span>
              </button>
              <button className="maps-modal-option" onClick={() => handleMapsModalSelect('google')}>
                <span className="maps-modal-icon">📍</span>
                <span>Google Maps</span>
              </button>
            </div>
            <button className="maps-modal-cancel" onClick={() => setShowMapsModal(false)}>
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default LGBTResourcesMap;